import React, { useState, useCallback, useEffect } from "react";
import { User } from "../types/User";
import { toast } from "react-toastify";
import { service } from "../../../services/AxiosServices";
import validator from "validator";
import Multiselect from "multiselect-react-dropdown";
import x from '../../static/images/x.png'
import { useLocation } from "react-router-dom";


interface UserModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (newUser: User) => void;
  editUser?: User | null;
  context?: 'manageUsers' | 'partner';
}

const UserModal: React.FC<UserModalProps> = ({
  show,
  onClose,
  onSave,
  editUser,
  context
}) => {
  const [formState, setFormState] = useState({
    values: {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      role_ids: [] as number[],
      knowledgeHub: [] as string[],
      crews: [] as string[],
    },
    clientErrors: {} as Record<string, string>,
    serverError: null as string | null,
  });

  const [roleOptions, setRoleOptions] = useState<
    { label: string; value: string; id?: number }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation()
  const partnerTab = location.pathname.includes('partner-tab')

  // Populate form when editing
  useEffect(() => {
    if (editUser) {
      // const roleIds = roleOptions
      //   .filter((role) => editUser.role?.split(", ").includes(role.value))
      //   .map((role) => role.id!)
      //   .filter((id) => id);

      setFormState((prev) => ({
        ...prev,
        values: {
          firstname: editUser.firstName || "",
          lastname: editUser.lastName || "",
          email: editUser.email || "",
          password: "",
          role_ids: [],
          knowledgeHub: editUser.knowledgeHub?.split(", ") || [],
          crews: editUser.crews?.split(", ") || [],
        },
      }));
    }
  }, [editUser, roleOptions]);

  useEffect(() => {
    console.log(editUser);
  }, [editUser]);
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        const response = await service<{ response: any[] }>(
          "/roles",
          "post",
          {}
        );
        const apiRoles = response.data.response
          .map((role: any) => ({
            label: role.name,
            value: role.name,
            id: Number(role.id),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setRoleOptions(apiRoles);
      } catch (err) {
        console.log("Error fetching roles:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const handleUserSelect = useCallback((selectedList: any[]) => {
    const selectedIds = selectedList.map((role) => role.id);
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, role_ids: selectedIds },
      clientErrors: { ...prev.clientErrors, role_ids: "" },
    }));
  }, []);

  const handleUserRemove = useCallback((removedList: any[]) => {
    const remainingIds = removedList.map((role) => role.id);
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, role_ids: remainingIds },
      clientErrors: { ...prev.clientErrors, role_ids: "" },
    }));
  }, []);

  const validations = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};

    if (!values.firstname.trim()) {
      errors.firstname = "First name is required.";
    }
    if (!values.lastname.trim()) {
      errors.lastname = "Last name is required.";
    }
    if (!values.email.trim()) {
      errors.email = "Email is required.";
    } else if (!validator.isEmail(values.email)) {
      errors.email = "Invalid email format";
    }
    // Only validate password for new users
    if (!editUser) {
      if (!values.password.trim()) {
        errors.password = "Password is required.";
      } else if (values.password.length < 12 || values.password.length > 128) {
        errors.password = "Password must be between 12 and 128 characters.";
      } else if (!/[A-Z]/.test(values.password)) {
        errors.password =
          "Password must include at least one uppercase letter.";
      } else if (!/[a-z]/.test(values.password)) {
        errors.password =
          "Password must include at least one lowercase letter.";
      } else if (!/\d/.test(values.password)) {
        errors.password = "Password must include at least one number.";
      } else if (
        !/[!@#?$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(values.password)
      ) {
        errors.password =
          "Password must include at least one special character (e.g., !, @, #, ?).";
      }
    }

    return errors;
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: value,
      },
      clientErrors: {
        ...prev.clientErrors,
        [name]: "",
      },
      serverError: null,
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const errors = validations(formState.values);

      if (Object.keys(errors).length === 0) {
        try {
          let requestBody;
          const endpoint = editUser ? "/edit-user" : "/register";

          if (editUser && editUser.id) {
            // For editing an existing user
            const formData = new FormData();
            formData.append("user", editUser.id.toString());
            formData.append("first_name", formState.values.firstname);
            formData.append("last_name", formState.values.lastname);
            formData.append(
              "is_active",
              (editUser.status === "active").toString()
            );

            // Use multipart/form-data content type for edit requests
            await service<any>(
              endpoint,
              "post",
              formData,
              "multipart/form-data"
            );

            const userId = editUser.id;
            const updatedUser = {
              id: userId,
              username: `${formState.values.firstname}_${formState.values.lastname}`,
              firstName: formState.values.firstname,
              lastName: formState.values.lastname,
              email: formState.values.email,
              role: roleOptions
                .filter((role) => formState.values.role_ids.includes(role.id!))
                .map((role) => role.value)
                .join(", "),
              status: "active",
              knowledgeHub: formState.values.knowledgeHub.join(", "),
              crews: formState.values.crews.join(", "),
            };

            onSave(updatedUser);
          } else {
            // For creating a new user
            requestBody = {
              project_id: localStorage.getItem("project_id"),
              application_id: localStorage.getItem("application"),
              created_updated_by: localStorage.getItem("user"),
              first_name: formState.values.firstname,
              last_name: formState.values.lastname,
              email: formState.values.email,
              password: formState.values.password,
              role_ids: formState.values.role_ids.join(","),
              partnercompany: partnerTab,
            };

            // Regular JSON for new user registration
            const response = await service<any>(endpoint, "post", requestBody);

            const userId = response?.data?.user?.id;
            const updatedUser = {
              id: userId,
              username: `${formState.values.firstname}_${formState.values.lastname}`,
              firstName: formState.values.firstname,
              lastName: formState.values.lastname,
              email: formState.values.email,
              role: roleOptions
                .filter((role) => formState.values.role_ids.includes(role.id!))
                .map((role) => role.value)
                .join(", "),
              status: "active",
              knowledgeHub: formState.values.knowledgeHub.join(", "),
              crews: formState.values.crews.join(", "),
            };

            onSave(updatedUser);
          }

          toast.success(
            `User ${editUser ? "updated" : "created"} successfully!`,
            { autoClose: 2000 }
          );
          handleModalClose();
        } catch (err: any) {
          const errorMessage =
            err.response?.data?.detail || (err.response?.data?.error && Object.values(err.response.data.error).flat()) ||
            "Something went wrong. Please try again.";

          console.log(errorMessage);
          setFormState((prev) => ({ ...prev, serverError: errorMessage }));
        }
      } else {
        setFormState((prev) => ({ ...prev, clientErrors: errors }));
      }
    },
    [formState.values, onSave, editUser, roleOptions]
  );
  const handleModalClose = () => {
    setFormState({
      values: {
        firstname: "",
        lastname: "",
        email: "",
        password: "",
        role_ids: [],
        knowledgeHub: [],
        crews: [],
      },
      clientErrors: {},
      serverError: null,
    });
    onClose();
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-overlay"></div>
      <div
        className="modal show d-block"
        tabIndex={-1}
        role="dialog"
        aria-hidden={!show}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          role="document"
        >
          <div className="modal-content p-4">
            <div className="modal-header border-0">
              <h5 className="modal-title">
                {editUser ? "Edit user" : "Add user"}
              </h5>

              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleModalClose}
              ></button>
            </div>
            {formState.serverError && (
              <div className="alert alert-danger mx-3 mt-2">
                {formState.serverError}
              </div>
            )}

            <div className="modal-body py-0">
              <form onSubmit={handleSubmit}>
                <div className="form-group mb-3">
                  <label htmlFor="formFirstName">
                    First name:<span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="formFirstName"
                    name="firstname"
                    value={formState.values.firstname}
                    onChange={handleChange}
                  />
                  {formState.clientErrors.firstname && (
                    <small className="text-danger">
                      {formState.clientErrors.firstname}
                    </small>
                  )}
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="formLastName">
                    Last name:<span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="formLastName"
                    name="lastname"
                    value={formState.values.lastname}
                    onChange={handleChange}
                  />
                  {formState.clientErrors.lastname && (
                    <small className="text-danger">
                      {formState.clientErrors.lastname}
                    </small>
                  )}
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="formEmail">
                    Email:<span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="formEmail"
                    name="email"
                    disabled={editUser ? true : false}
                    value={formState.values.email}
                    onChange={handleChange}
                  />
                  {formState.clientErrors.email && (
                    <small className="text-danger">
                      {formState.clientErrors.email}
                    </small>
                  )}
                </div>

                {!editUser && (
                  <div className="form-group mb-3">
                    <label htmlFor="formPassword">
                      Password:<span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="formPassword"
                      name="password"
                      value={formState.values.password}
                      onChange={handleChange}
                    />
                    {formState.clientErrors.password && (
                      <small className="text-danger">
                        {formState.clientErrors.password}
                      </small>
                    )}
                  </div>
                )}

                {!editUser && context !== 'partner'&& (
                  <>
                    <label htmlFor="role_ids" className="LabelText mb-1">
                      Select roles:
                    </label>
                    <div className="custom-multiselect">
                      <Multiselect
                        options={roleOptions}
                        selectedValues={roleOptions.filter((role) =>
                          formState.values.role_ids.includes(role.id!)
                        )}
                        onSelect={handleUserSelect}
                        onRemove={handleUserRemove}
                        displayValue="label"
                        customCloseIcon={<img style={{ width: "1rem", height: "1rem", marginLeft:'1rem' }} src={x} alt="close" />}
                      />
                    </div>
                  </>
                )}
                <div className="modal-footer border-0 p-0 mt-3">
                  <button type="submit" className="btn SaveBtn m-0">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserModal;
